import API from 'shared/api/API';
import { DELETE, GET, POST, PUT } from 'shared/utils/Executer';
import { USER_SERVICE_URL } from 'shared/constants/APIconstants';

class UserAPI {
  /**
   * Merge two users by their email addresses
   * @param {Object} params
   * @param {string} params.primaryEmail - Primary user email (data will be merged into this user)
   * @param {string} params.secondaryEmail - Secondary user email (this user will be deleted after merge)
   * @returns {Promise<{user: Object, migrationStats: Object}>} Merged user and migration statistics
   */
  async mergeUsers({ primaryEmail, secondaryEmail }) {
    return await API.client.execute(POST, `${USER_SERVICE_URL}merge-users`, {
      primaryEmail,
      secondaryEmail,
    });
  }

  /**
   * Get user data with their subscriptions by email
   * @param {string} email - User email address
   * @returns {Promise<{user: Object, subscriptions: Array, primaryOrganization: Object}>}
   */
  async getUserWithSubscriptions(email) {
    return await API.client.execute(
      GET,
      `${USER_SERVICE_URL}subscriptions?email=${encodeURIComponent(email)}`,
    );
  }

  /**
   * Find users whose primary or secondary email matches, including deleted accounts (ROOT only)
   * @param {Object} params
   * @param {string} params.email - Email address to look up
   * @returns {Promise<{data: any, error?: any}>} Matching users with their organizations
   */
  async findUsersByEmail({ email }) {
    return await API.client.execute(
      GET,
      `${USER_SERVICE_URL}emails?email=${encodeURIComponent(email)}`,
    );
  }

  /**
   * Add a secondary email to a user (ROOT only). Responds 409 when another user holds the address
   * @param {Object} params
   * @param {string} params.userId - Target user id
   * @param {string} params.email - Email to add
   * @param {string} params.reason - Ticket or reason, recorded in the audit log
   * @returns {Promise<{data: any, error?: any}>} Updated user
   */
  async addSecondaryEmail({ userId, email, reason }) {
    return await API.client.execute(POST, `${USER_SERVICE_URL}${userId}/secondary-emails`, {
      email,
      reason,
    });
  }

  /**
   * Remove a secondary email from a user (ROOT only)
   * @param {Object} params
   * @param {string} params.userId - Target user id
   * @param {string} params.email - Secondary email to remove
   * @param {string} params.reason - Ticket or reason, recorded in the audit log
   * @returns {Promise<{data: any, error?: any}>} Updated user
   */
  async removeSecondaryEmail({ userId, email, reason }) {
    return await API.client.execute(
      DELETE,
      `${USER_SERVICE_URL}${userId}/secondary-emails/${encodeURIComponent(email)}`,
      { data: { reason } },
    );
  }

  /**
   * Change a user's primary email, or preview the change with dryRun (ROOT only)
   * @param {Object} params
   * @param {string} params.userId - Target user id
   * @param {string} params.email - New primary email
   * @param {string} params.oldEmail - Primary email as shown when the change was started; lets the backend resume an interrupted change and reject a stale one
   * @param {boolean} params.keepOldAsSecondary - Keep the old primary as a secondary email
   * @param {string} [params.reason] - Ticket or reason, required unless dryRun
   * @param {boolean} params.dryRun - Only count the records that would change
   * @returns {Promise<{data: any, error?: any}>} Dry run: `{ counts }`. Applied: `{ counts, user, updated, oneSignalSynced }`, where counts are the records still carrying the old address
   */
  async changePrimaryEmail({ userId, email, oldEmail, keepOldAsSecondary, reason, dryRun }) {
    return await API.client.execute(PUT, `${USER_SERVICE_URL}${userId}/primary-email`, {
      email,
      oldEmail,
      keepOldAsSecondary,
      reason,
      dryRun,
    });
  }
}

export default new UserAPI();
